import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import JobTracker from './components/JobTracker';
import TaskView from './components/TaskView';
import Files from './components/Files';
import Profile from './components/Profile';
import Settings from './components/Settings';
import OnboardingFlow from './components/OnboardingFlow';
import OnboardingSuccess from './components/OnboardingSuccess';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/dashboard" element={<SmartProtectedRoute><Dashboard /></SmartProtectedRoute>} />
            <Route path="/job-tracker" element={<SmartProtectedRoute><JobTracker /></SmartProtectedRoute>} />
            <Route path="/tasks" element={<SmartProtectedRoute><TaskView /></SmartProtectedRoute>} />
            <Route path="/documents" element={<SmartProtectedRoute><Files /></SmartProtectedRoute>} />
            <Route path="/profile" element={<SmartProtectedRoute><Profile /></SmartProtectedRoute>} />
            <Route path="/settings" element={<SmartProtectedRoute><Settings /></SmartProtectedRoute>} />
            <Route path="/onboarding" element={<OnboardingProtectedRoute><OnboardingFlow /></OnboardingProtectedRoute>} />
            <Route path="/onboarding/success" element={<OnboardingProtectedRoute><OnboardingSuccess /></OnboardingProtectedRoute>} />
            <Route path="/resubscribe" element={<ResubscribeRoute><OnboardingFlow /></ResubscribeRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/app" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

// Protected route that requires authentication only
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  return user ? children : <Navigate to="/login" />;
}

// Smart protected route that checks both authentication and journey stage
function SmartProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Debug logging for routing decisions
  console.log('🔍 SmartProtectedRoute - User journey stage:', user.journeyStage, 'Role:', user.role, 'Email:', user.email);
  
  // Admin bypass - admins can access everything regardless of subscription status
  if (user.role === 'admin' || user.role === 'superadmin') {
    console.log('🔧 Admin bypass - allowing access');
    return children;
  }
  
  // Regular user routing based on journey stage
  switch (user.journeyStage) {
    case 'onboarding':
      console.log('❌ Redirecting to onboarding');
      return <Navigate to="/onboarding" />;
      
    case 'trial':
    case 'active':
    case 'cancelling':
      console.log('✅ Allowing access - journey stage:', user.journeyStage);
      return children;
      
    case 'expired':
      console.log('💔 Redirecting expired user to resubscribe');
      return <Navigate to="/resubscribe" />;
      
    default:
      console.log('🤷 Unknown journey stage, redirecting to onboarding');
      return <Navigate to="/onboarding" />;
  }
}

// Onboarding protected route that allows access to onboarding flow but redirects completed users
function OnboardingProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Admin bypass - allow admins to access onboarding for testing
  if (user.role === 'admin' || user.role === 'superadmin') {
    console.log('🔧 Admin accessing onboarding for testing');
    return children;
  }
  
  // If user has active access, redirect to dashboard
  if (['trial', 'active', 'cancelling'].includes(user.journeyStage)) {
    return <Navigate to="/dashboard" />;
  }
  
  // Allow onboarding and expired users to access onboarding flow
  return children;
}

// Resubscribe route for expired users
function ResubscribeRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Admin bypass
  if (user.role === 'admin' || user.role === 'superadmin') {
    return children;
  }
  
  // If user has active access, redirect to dashboard
  if (['trial', 'active', 'cancelling'].includes(user.journeyStage)) {
    return <Navigate to="/dashboard" />;
  }
  
  // If user is still onboarding, redirect to onboarding
  if (user.journeyStage === 'onboarding') {
    return <Navigate to="/onboarding" />;
  }
  
  // Allow expired users to resubscribe
  return children;
}

// Admin-only route
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
}

// Simple Admin Dashboard component
function AdminDashboard() {
  return (
    <div className="min-h-screen bg-gradient-main p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>
        <div className="bg-white bg-opacity-10 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Coming Soon</h2>
          <p className="text-gray-300">
            The admin dashboard frontend is under development. 
            Use the API endpoints directly for now:
          </p>
          <ul className="text-gray-300 mt-4 space-y-2">
            <li>• GET /api/admin/dashboard - View stats</li>
            <li>• GET /api/admin/users - List users</li>
            <li>• POST /api/admin/impersonate/:userId - Impersonate user</li>
            <li>• PATCH /api/admin/users/:userId/role - Change user role</li>
            <li>• PATCH /api/admin/users/:userId/journey-stage - Update journey stage</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default App;